import React from 'react';
import { format, subDays } from 'date-fns';
import { Report as PlatformReport, Page } from 'cccisd-laravel-report';
import { ResponsiveLine } from 'cccisd-nivo/line';
import Images from './Images/index.js';
import style from './style.css';

const colors = {
    green: '#65B045',
    red: '#A41F22',
    activity: '#192752',
    calorie: '#F2CB17',
    weight: '#000000',
    goal: '#999999',
    checkboxData: '#622466',
};

const headerText = {
    green: 'GREEN Foods',
    red: 'RED Foods',
    activity: 'Activity',
    calorie: 'Calories',
    weight: 'Weight',
    checkboxData: 'Family Encouragement',
};

const headerIcon = {
    green: Images.IconGreen,
    red: Images.IconRed,
    activity: Images.IconActivity,
    calorie: Images.IconCalorie,
    weight: Images.IconWeight,
    checkboxData: Images.IconFamily,
};

const Report = ({ reportData, displayName, numOfDays }) => {
    if (!Array.isArray(reportData?.goals) || !Array.isArray(reportData?.dailyTracking)) {
        return (
            <div className="alert alert-warning">
                <p className="mb-0">No data to display.</p>
            </div>
        );
    }

    const now = new Date();
    let cutoffDate = '';
    const baseDataMap = {};
    for (let i = 0; i < numOfDays; i++) {
        const then = subDays(now, i);
        const dateStr = format(then, 'yyyy-MM-dd');
        if (i === numOfDays - 1) {
            cutoffDate = dateStr;
        }
        baseDataMap[dateStr] = null;
    }

    // key is date (x-value), val is number (y-value)
    let greenGoalDataMap = { ...baseDataMap };
    let redGoalDataMap = { ...baseDataMap };
    let activityGoalDataMap = { ...baseDataMap };
    let calorieGoalDataMap = { ...baseDataMap };
    let weightGoalDataMap = { ...baseDataMap };
    reportData.goals.forEach(goalSubmission => {
        if (!goalSubmission?.completedAt) {
            return;
        }

        let date = goalSubmission.completedAt.match(/^[\d]{4}-[\d]{2}-[\d]{2}/);
        if (!Array.isArray(date) || date.length === 0) {
            return;
        }

        date = date[0];
        const { devTags } = goalSubmission;

        /* rely on graphql sort to ensure we're looping from oldest to newest.
        This means it's always ok to overwrite goal entry from the same day. */
        if ('goalsGreenFood' in devTags && (devTags.goalsGreenFood || devTags.goalsGreenFood === 0)) {
            greenGoalDataMap[date] = parseInt(devTags.goalsGreenFood, 10);
        }
        if ('goalsRedFood' in devTags && (devTags.goalsRedFood || devTags.goalsRedFood === 0)) {
            redGoalDataMap[date] = parseInt(devTags.goalsRedFood, 10);
        }
        if ('goalsActivity' in devTags && (devTags.goalsActivity || devTags.goalsActivity === 0)) {
            activityGoalDataMap[date] = parseInt(devTags.goalsActivity, 10);
        }
        if ('goalsCalorie' in devTags && (devTags.goalsCalorie || devTags.goalsCalorie === 0)) {
            calorieGoalDataMap[date] = parseInt(devTags.goalsCalorie, 10);
        }
    });

    let greenDailyTrackingDataMap = {};
    let redDailyTrackingDataMap = {};
    let activityDailyTrackingDataMap = {};
    let calorieDailyTrackingDataMap = {};
    let weightDailyTrackingDataMap = {};
    let checkInDailyTrackingDataMap = {};
    let praiseDailyTrackingDataMap = {};
    reportData.dailyTracking.forEach(dtSubmission => {
        const { devTags } = dtSubmission;
        if (!devTags?.dailyTrackingDate) {
            return;
        }

        const date = devTags.dailyTrackingDate;

        /* rely on graphql sort to ensure we're looping from oldest to newest.
        This means it's always ok to overwrite goal entry from the same day. */
        if ('dailyTrackingGreen' in devTags && (devTags.dailyTrackingGreen || devTags.dailyTrackingGreen === 0)) {
            greenDailyTrackingDataMap[date] = parseInt(devTags.dailyTrackingGreen, 10);
        }
        if ('dailyTrackingRed' in devTags && (devTags.dailyTrackingRed || devTags.dailyTrackingRed === 0)) {
            redDailyTrackingDataMap[date] = parseInt(devTags.dailyTrackingRed, 10);
        }
        if (
            'dailyTrackingActivity' in devTags &&
            (devTags.dailyTrackingActivity || devTags.dailyTrackingActivity === 0)
        ) {
            activityDailyTrackingDataMap[date] = parseInt(devTags.dailyTrackingActivity, 10);
        }
        if (
            'dailyTrackingCalories' in devTags &&
            (devTags.dailyTrackingCalories || devTags.dailyTrackingCalories === 0)
        ) {
            calorieDailyTrackingDataMap[date] = parseInt(devTags.dailyTrackingCalories, 10);
        }
        if (
            'dailyTrackingWeighAmount' in devTags &&
            (devTags.dailyTrackingWeighAmount || devTags.dailyTrackingWeighAmount === 0)
        ) {
            weightDailyTrackingDataMap[date] = parseInt(devTags.dailyTrackingWeighAmount, 10);
        }
        if ('dailyTrackingCheckIn' in devTags && devTags.dailyTrackingCheckIn === 'checked') {
            checkInDailyTrackingDataMap[date] = true;
        }
        if ('dailyTrackingPraise' in devTags && devTags.dailyTrackingPraise === 'checked') {
            praiseDailyTrackingDataMap[date] = true;
        }
    });

    function renderHeader(category) {
        const isSvg = typeof headerIcon[category] === 'function';
        let ImageComponent = isSvg
            ? headerIcon[category]
            : () => <img src={headerIcon[category]} alt={category + ' logo'} />;

        return (
            <h3
                className={`mb-0 ${style.header} ${style.fsLibre}`}
                style={{ borderBottomColor: colors[category], paddingBottom: isSvg ? 5 : 0 }}
            >
                <span style={{ color: colors[category] }} className={style.headerImage}>
                    {<ImageComponent />}
                </span>
                {headerText[category]}
            </h3>
        );
    }

    function renderLineGraph(category) {
        let id;
        let goalMap;
        let dtMap;
        let yLegend;
        let color = colors[category];
        let yAboveMaxAmount = 0;
        switch (category) {
            case 'green':
                id = 'GREEN Foods';
                goalMap = greenGoalDataMap;
                dtMap = greenDailyTrackingDataMap;
                yLegend = 'Servings';
                yAboveMaxAmount = 2;
                break;
            case 'red':
                id = 'RED Foods';
                goalMap = redGoalDataMap;
                dtMap = redDailyTrackingDataMap;
                yLegend = 'Servings';
                yAboveMaxAmount = 2;
                break;
            case 'activity':
                id = 'Activity';
                goalMap = activityGoalDataMap;
                dtMap = activityDailyTrackingDataMap;
                yLegend = 'Minutes';
                yAboveMaxAmount = 20;
                break;
            case 'calorie':
                id = 'Calories';
                goalMap = calorieGoalDataMap;
                dtMap = calorieDailyTrackingDataMap;
                yLegend = 'kcal';
                yAboveMaxAmount = 200;
                break;
            case 'weight':
                id = 'Weight';
                goalMap = weightGoalDataMap;
                dtMap = weightDailyTrackingDataMap;
                yLegend = 'Pounds';
                yAboveMaxAmount = 20;
                break;
            default:
                return;
        }

        let yMax = 0;
        let firstGoal = null;
        const goalData = Object.keys(goalMap)
            .sort()
            .reduce((all, curr) => {
                let val = goalMap[curr];
                if (val !== null && val > yMax) {
                    yMax = val;
                }

                if (val !== null && !firstGoal) {
                    firstGoal = val;
                }

                if (val === null && all.length > 0) {
                    val = all[all.length - 1].y;
                }

                all.push({ x: curr, y: val });
                return all;
            }, [])
            .map(d => {
                if (d.y === null) {
                    d.y = firstGoal;
                }

                return d;
            });

        const dtData = Object.keys(dtMap)
            .reduce((all, curr) => {
                if (curr.localeCompare(cutoffDate) < 0) {
                    return all;
                }

                const val = dtMap[curr];
                if (val > yMax) {
                    yMax = val;
                }

                all.push({ x: curr, y: val });
                return all;
            }, [])
            .sort((a, b) => a.x.localeCompare(b.x));

        let xNumOfTicks = 7;
        if (numOfDays > 7) {
            xNumOfTicks = 15;
        }

        return (
            <>
                {renderHeader(category, color)}
                <div style={{ height: '350px', width: '750px' }} className="mb-5">
                    <ResponsiveLine
                        data={[
                            {
                                id: 'Goal',
                                color: colors.goal,
                                data: goalData,
                            },
                            {
                                id,
                                color,
                                data: dtData,
                            },
                        ]}
                        xScale={{
                            type: 'time',
                            format: '%Y-%m-%d',
                            precision: 'day',
                            useUTC: false,
                        }}
                        xFormat="time:%m-%d"
                        axisBottom={{
                            format: '%m-%d',
                            tickRotation: -45,
                            tickValues: xNumOfTicks,
                            legend: 'Date',
                            legendOffset: 40,
                            legendPosition: 'middle',
                        }}
                        yScale={{
                            type: 'linear',
                            min: 0,
                            max: yMax + yAboveMaxAmount,
                        }}
                        axisLeft={{
                            legend: yLegend,
                            legendOffset: -42,
                            legendPosition: 'middle',
                        }}
                        colors={d => d.color}
                        enablePoints
                        pointSize={numOfDays > 60 ? 8 : 15}
                        pointSymbol={pointProps => {
                            if (pointProps.color === colors.goal) {
                                return null;
                            }

                            return (
                                <circle
                                    r={pointProps.size / 2}
                                    fill={pointProps.color}
                                    stroke={pointProps.borderColor}
                                    strokeWidth={pointProps.borderWidth}
                                />
                            );
                        }}
                        useMesh
                        enableGridX
                        enableGridY
                        margin={{ top: 30, right: 130, bottom: 50, left: 50 }}
                        layers={[
                            'grid',
                            'markers',
                            'axes',
                            'areas',
                            // Override default line drawing with our custom function
                            lineProps => {
                                const { series } = lineProps;
                                function isValid(v) {
                                    return !Number.isNaN(parseInt(v, 10));
                                }

                                return series.map(s => {
                                    const isGoalLine = s.id === 'Goal';
                                    let thickness = 6;
                                    if (numOfDays > 60) {
                                        thickness = 3;
                                    }

                                    const pathD = s.data.reduce(
                                        (all, currPoint, i) => {
                                            const pos = currPoint.position;
                                            if (!isValid(pos.x) || (!isValid(pos.y) && !isGoalLine)) {
                                                return all;
                                            }

                                            const nextPos = i < s.data.length - 1 ? s.data[i + 1].position : null;
                                            const { d, prevY } = all;
                                            let startX = parseInt(pos.x, 10);
                                            let startY = '';
                                            let endX = '';
                                            let endY = '';
                                            if (isGoalLine) {
                                                startY = isValid(pos?.y) ? parseInt(pos.y, 10) : prevY;
                                                endY = startY;
                                                endX = isValid(nextPos?.x) && parseInt(nextPos.x, 10);
                                                /* if goal has changed TODAY, end of graph should extend slightly
                                                     to show new goal */
                                                if (
                                                    i === s.data.length - 1 &&
                                                    isValid(s.data[i - 1]?.position?.y) &&
                                                    s.data[i - 1].position.y !== startY
                                                ) {
                                                    endX = startX + (numOfDays < 35 ? 20 : 10);
                                                }
                                            } else {
                                                startY = isValid(pos.y) && parseInt(pos.y, 10);
                                                endX = isValid(nextPos?.x) && parseInt(nextPos.x, 10);
                                                endY = isValid(nextPos?.y) && parseInt(nextPos.y, 10);
                                            }

                                            if (isValid(startX) && isValid(startY) && isValid(endX) && isValid(endY)) {
                                                return { d: d + `M${startX} ${startY} L ${endX} ${endY}`, prevY: endY };
                                            }
                                            return all;
                                        },
                                        { d: '', prevY: null }
                                    );

                                    return (
                                        <path
                                            key={s.id}
                                            d={pathD.d}
                                            fill="none"
                                            stroke={s.color}
                                            strokeWidth={thickness}
                                        />
                                    );
                                });
                            },
                            'slices',
                            'points',
                            legendProps => {
                                const isHideGoal = legendProps.series.some(s => ['Weight'].includes(s.id));
                                const textStyles = {
                                    fontFamily: 'sans-serif',
                                    fontSize: '11px',
                                    fill: 'rgb(51, 51, 51)',
                                    dominantBaseline: 'central',
                                };

                                const circleProps = {
                                    r: 6,
                                    cx: 6,
                                    cy: 10,
                                    opacity: 1,
                                    strokeWidth: 0,
                                    stroke: 'rgba(0, 0, 0, 0.5)',
                                };

                                return (
                                    <g transform="translate(590,280)">
                                        {legendProps.series.map((s, i) => {
                                            if (s.id === 'Goal' && isHideGoal) {
                                                return null;
                                            }

                                            return (
                                                <g transform={`translate(0,${20 * i})`} key={s.id}>
                                                    <circle {...circleProps} fill={s.color} />
                                                    <text textAnchor="start" x="20" y="10" style={textStyles}>
                                                        {s.id}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                    </g>
                                );
                            },
                        ]}
                    />
                </div>
            </>
        );
    }

    function renderFrequencyGraph() {
        const markerSize = numOfDays < 31 ? 16 : 12;

        let isTooManyDays = numOfDays > 60;

        let numDaysThisGraph = numOfDays;
        let dates = Object.keys(baseDataMap).sort();
        if (isTooManyDays) {
            numDaysThisGraph = 60;
            dates = dates.slice(dates.length - 60);
        }

        return (
            <>
                {renderHeader('checkboxData')}
                {isTooManyDays && (
                    <p className="my-3 fst-italic fw-lighter">Note: This data is limited to the past 60 days.</p>
                )}
                <table className={style.frequencyTable}>
                    <tbody>
                        <tr>
                            <td>Praise</td>
                            {dates.map(dateString => {
                                if (praiseDailyTrackingDataMap[dateString]) {
                                    return (
                                        <td key={dateString} style={{ fontSize: markerSize }}>
                                            <span className={style.star}>
                                                <Images.IconStar />
                                            </span>
                                        </td>
                                    );
                                }
                                return <td key={dateString} />;
                            })}
                        </tr>
                        <tr>
                            <td>Check-In</td>
                            {dates.map(dateString => {
                                if (checkInDailyTrackingDataMap[dateString]) {
                                    return (
                                        <td key={dateString} style={{ fontSize: markerSize }}>
                                            <span className={style.checkmark}>
                                                <Images.IconCheck />
                                            </span>
                                        </td>
                                    );
                                }
                                return <td key={dateString} />;
                            })}
                        </tr>
                        <tr className={style.dateRow}>
                            <td />
                            {dates.map((dateString, i) => {
                                let everyOtherDivisor = 1;
                                if (numDaysThisGraph > 40) {
                                    everyOtherDivisor = 3;
                                } else if (numDaysThisGraph > 10) {
                                    everyOtherDivisor = 2;
                                }

                                const cellWidth = (750 - 60) / numDaysThisGraph;

                                return (
                                    <td
                                        key={dateString}
                                        className={style.dateCells}
                                        style={{ left: 60 + i * cellWidth + cellWidth / 2 - 22 }}
                                    >
                                        {i % everyOtherDivisor === 0 && dateString.replace(/^[\d]{4}-/, '')}
                                    </td>
                                );
                            })}
                        </tr>
                    </tbody>
                </table>
            </>
        );
    }

    let titleDuration = numOfDays + '-day';
    if (numOfDays > 100) {
        titleDuration = '6-month';
    } else if (numOfDays > 61) {
        titleDuration = '3-month';
    }

    return (
        <PlatformReport downloadFilename={`Tracking Report - ${displayName}`}>
            <Page>
                <div className="mb-5 d-flex justify-content-between">
                    <h2 className={style.fsLibre}>
                        {titleDuration} Daily Tracking report for {displayName}
                    </h2>
                    <img src={Images.IconBeans} alt="together for health logo" className={style.bean} />
                </div>
                {renderLineGraph('weight')}
                {renderLineGraph('calorie')}
            </Page>
            <Page>
                <div className="pt-5">
                    {renderLineGraph('green')}
                    {renderLineGraph('red')}
                </div>
            </Page>
            <Page>
                <div className="pt-5">
                    {renderLineGraph('activity')}
                    <div className={style.bigMarginTop}>{renderFrequencyGraph()}</div>
                </div>
            </Page>
        </PlatformReport>
    );
};

export default Report;
